import urlUtils from './urlUtils';

export const HTTP_ERROR_CODES = {
  TIMEOUT: -10086,
  FAILED_TO_FETCH: -1,
};

const CSRF_HEADER_NAME = 'X-XSRF-TOKEN';

function generateCsrfCode() {
  // 优先取腾讯云 skey，若没有则取 SaaS 的 cloudstudio-session
  const skey = urlUtils.getCookie('skey') || urlUtils.getCookie('cloudstudio-session');
  if (!skey) {
    return '';
  }
  let hash = 5381;
  for (let i = 0, len = skey.length; i < len; ++i) {
    hash += (hash << 5) + skey.charCodeAt(i);
  }
  const csrfCode = hash & 0x7fffffff;
  return csrfCode;
}

export function appendXSRFTokenHeader(headers) {
  const xsrfToken = generateCsrfCode();
  if (!xsrfToken) {
    return headers;
  }
  return {
    ...headers,
    [CSRF_HEADER_NAME]: xsrfToken,
  };
}

const httpClient = (() => {
  // const BACKEND_URL = `/api`;
  const defaultTimeout = 20 * 1000;

  function parse(res) {
    if (!res || (res && (res.status === 204 || res.status === 205))) {
      return null;
    }

    return res.json().then((serialized) => serialized);
  }

  function createAbortablePromise(timeout, defaultRejectMessage = 'CS_TIMEOUT_Abort') {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject({
          code: HTTP_ERROR_CODES.TIMEOUT,
          message: defaultRejectMessage,
        });
        clearTimeout(timer);
      }, timeout);
    });
  }

  const headerInfo = {
    redirect: 'follow',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  };

  const wsToken = urlUtils.getWsToken();
  if (wsToken) {
    headerInfo.headers['Authorization'] = `Bearer ${wsToken}`;
  }

  return function (data) {
    const path = data.path;
    const method = data.method || 'GET';
    let headers = data.headers || {};
    const body = data.body || {};
    const isCarryBackendUrl = typeof data.isCarryBackendUrl === 'undefined' ? true : data.isCarryBackendUrl;
    const abortable = createAbortablePromise(
      data.path.includes('/customize/templates') ||
        (data.path.includes('customize') && data.path.includes('workspaces'))
        ? 60 * 1000
        : defaultTimeout,
      'CS_请求超时',
    );
    // csrf 安全头处理
    if (!headers[CSRF_HEADER_NAME]) {
      headers = appendXSRFTokenHeader(headers);
    }
    const allHeader = {
      method,
      ...headerInfo,
      headers: {
        ...headerInfo.headers,
        ...headers,
      },
    };
    let traceId = '';
    const fetchPromise = new Promise((resolve, reject) => {
      fetch(
        isCarryBackendUrl ? `/api${path}` : path,
        method === 'POST' || method === 'PUT' ? { ...allHeader, body: JSON.stringify(body) } : { ...allHeader },
      )
        .then((res) => {
          traceId = res.headers.get('X-TRACE-ID');
          // 这里是控制跳转
          if (res.headers.get('IDE-REDIRECT-URL')) {
            window.location.href = res.headers.get('IDE-REDIRECT-URL');
            reject({
              message: res.statusText || '401 redirect',
              traceId,
              code: res.status,
              path,
            });
          }
          if (res.status >= 400 || res.status >= 500) {
            const errMessagePrefix = res.status >= 400 && res.status < 500 ? '请求出错' : '服务出错';
            reject({
              code: res.status,
              message: `${errMessagePrefix}: ${path} ${res.statusText || ''}`,
              traceId,
              path,
            });
          }
          // /tty 探测请求 包含 302 重定向的情况
          // 参考 https://dev-qa.com/774109/how-to-do-302-redirect-fetch
          if (path.startsWith(`${location.origin}/tty/`) && res.status >= 200 && res.status < 400) {
            resolve({
              code: 0,
              message: 'tty正常',
              redirected: res.redirected,
              url: res.url,
              traceId,
              path,
            });
          }
          // run 接口
          else if (/workspace\/\w+\/run/.test(path)) {
            if (traceId) {
              // 缓存 traceId 属性
              setTimeout(() => {
                // 不阻塞主线程
                sessionStorage.setItem('cloudstudio-traceId', traceId);
              }, 0);
            }
          }
          return res;
        })
        .then(parse)
        .then((response) => {
          if (response) {
            if (response.code === -2) {
              reject({ code: -2, message: 'System Error.', traceId, path });
            }

            // 腾讯云异常账号问题， 使用qq登录在未完成注册访问页面时，重定向到腾讯云注册流程界面 QCLOUD_USER_INVALID(8003, "Qcloud User Invalid")
            if (response.code === 8003) {
              const redirectUrl = encodeURIComponent(window.location.href);
              window.location.href = `https://cloud.tencent.com/register/completion?s_url=${redirectUrl}`;
            }

            resolve({
              ...response,
              traceId,
              path,
            });
          }
        })
        .catch((err) => {
          reject({
            code: HTTP_ERROR_CODES.FAILED_TO_FETCH,
            message: JSON.stringify(err, Object.getOwnPropertyNames(err)),
            traceId, // 这里获取不到 traceId，应该是初始值 ""
            path,
          });
        });
    });

    return Promise.race([abortable, fetchPromise])
      .then((res) => res)
      .catch((err) => {
        if (err.code && err.code === HTTP_ERROR_CODES.TIMEOUT) {
          return {
            ...err,
            traceId,
            path,
          };
        }
        return err;
      });
  };
})();

export default httpClient;
